/**
 * Durable, provider-neutral handoffs between the chat, work and video surfaces.
 *
 * This module deliberately stops at a typed delivery descriptor. In particular,
 * preparing a video handoff never calls the paid video create RPC. The video UI
 * must render the returned draft and require an explicit generation action.
 */

export const WORKFLOW_HANDOFF_SCHEMA_VERSION = 1 as const;
export const WORKFLOW_HANDOFF_STORAGE_PREFIX = "bianhua-workflow-handoffs:v1";

export type WorkflowSurface = "chat" | "work" | "video";
export type WorkflowHandoffPurpose =
  | "idea_to_video_prompt"
  | "video_prompt_to_generation"
  | "review_video"
  | "edit_video";
export type WorkflowHandoffStatus = "staged" | "ready" | "completed" | "dismissed";

export type WorkflowSource = {
  surface: WorkflowSurface;
  projectId: string;
  threadId: string;
  turnId?: string;
  itemId?: string;
  artifactId?: string;
};

export type WorkflowTarget = {
  surface: WorkflowSurface;
  projectId: string;
  threadId?: string;
};

/**
 * A reference to an already trusted asset. Network URLs and absolute paths are
 * intentionally absent from the contract. `relativePath` is always relative to
 * the configured project workspace.
 */
export type WorkflowArtifactRef = {
  assetId: string;
  artifactId?: string;
  relativePath: string;
  mediaType?: string;
  sha256?: string;
};

type WorkflowHandoffBase = {
  schemaVersion: typeof WORKFLOW_HANDOFF_SCHEMA_VERSION;
  handoffId: string;
  idempotencyKey: string;
  correlationId: string;
  principalId: string;
  projectId: string;
  source: WorkflowSource;
  target: WorkflowTarget;
  objective: string;
  text: string;
  assetIds: string[];
  artifactRefs: WorkflowArtifactRef[];
  createdAt: string;
  updatedAt: string;
  status: WorkflowHandoffStatus;
};

export type IdeaToVideoPromptHandoff = WorkflowHandoffBase & {
  purpose: "idea_to_video_prompt";
  source: WorkflowSource & { surface: "chat" };
  target: WorkflowTarget & { surface: "work" };
};

export type VideoPromptToGenerationHandoff = WorkflowHandoffBase & {
  purpose: "video_prompt_to_generation";
  source: WorkflowSource & { surface: "work" };
  target: WorkflowTarget & { surface: "video" };
};

export type VideoToWorkHandoff = WorkflowHandoffBase & {
  purpose: "review_video" | "edit_video";
  source: WorkflowSource & { surface: "video" };
  target: WorkflowTarget & { surface: "work" };
};

export type WorkflowHandoff =
  | IdeaToVideoPromptHandoff
  | VideoPromptToGenerationHandoff
  | VideoToWorkHandoff;

type NewHandoffBase = {
  idempotencyKey: string;
  correlationId: string;
  principalId: string;
  projectId: string;
  source: WorkflowSource;
  target: WorkflowTarget;
  objective: string;
  text: string;
  assetIds?: readonly string[];
  artifactRefs?: readonly WorkflowArtifactRef[];
};

export type NewWorkflowHandoff =
  | (NewHandoffBase & {
      purpose: "idea_to_video_prompt";
      source: WorkflowSource & { surface: "chat" };
      target: WorkflowTarget & { surface: "work" };
    })
  | (NewHandoffBase & {
      purpose: "video_prompt_to_generation";
      source: WorkflowSource & { surface: "work" };
      target: WorkflowTarget & { surface: "video" };
    })
  | (NewHandoffBase & {
      purpose: "review_video" | "edit_video";
      source: WorkflowSource & { surface: "video" };
      target: WorkflowTarget & { surface: "work" };
    });

export type VideoDraftDelivery = {
  kind: "video_draft";
  handoffId: string;
  correlationId: string;
  prompt: string;
  objective: string;
  assetIds: string[];
  artifactRefs: WorkflowArtifactRef[];
  billingAuthorized: false;
  requiresExplicitGeneration: true;
};

export type WorkDraftDelivery = {
  kind: "work_draft";
  handoffId: string;
  correlationId: string;
  text: string;
  objective: string;
  assetIds: string[];
  artifactRefs: WorkflowArtifactRef[];
  submit: false;
};

export type WorkExplicitSubmitDelivery = {
  kind: "work_explicit_submit";
  handoffId: string;
  correlationId: string;
  text: string;
  objective: string;
  assetIds: string[];
  artifactRefs: WorkflowArtifactRef[];
  submit: true;
};

export type WorkflowDelivery =
  | VideoDraftDelivery
  | WorkDraftDelivery
  | WorkExplicitSubmitDelivery;

export type WorkDeliveryDisposition = "draft" | "explicit-submit";

export type WorkflowHandoffEvent =
  | { type: "recovered"; handoffs: WorkflowHandoff[] }
  | { type: "staged"; handoff: WorkflowHandoff }
  | { type: "delivery-ready"; handoff: WorkflowHandoff; delivery: WorkflowDelivery }
  | { type: "completed"; handoff: WorkflowHandoff }
  | { type: "dismissed"; handoff: WorkflowHandoff };

export type WorkflowHandoffErrorCode =
  | "invalid_input"
  | "scope_mismatch"
  | "idempotency_conflict"
  | "not_found"
  | "invalid_transition"
  | "invalid_journal"
  | "persistence_failed";

export class WorkflowHandoffError extends Error {
  readonly code: WorkflowHandoffErrorCode;

  constructor(code: WorkflowHandoffErrorCode, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "WorkflowHandoffError";
    this.code = code;
  }
}

export interface WorkflowHandoffStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type WorkflowHandoffStoreOptions = {
  principalId: string;
  projectId: string;
  storage?: WorkflowHandoffStorage;
  now?: () => string;
  createId?: () => string;
};

type JournalEntry = {
  requestFingerprint: string;
  handoff: WorkflowHandoff;
  delivery?: WorkflowDelivery;
};

type HandoffJournal = {
  schemaVersion: typeof WORKFLOW_HANDOFF_SCHEMA_VERSION;
  principalId: string;
  projectId: string;
  revision: number;
  entries: JournalEntry[];
};

type Listener = (event: WorkflowHandoffEvent) => void;
type UnknownRecord = Record<string, unknown>;

const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const MEDIA_TYPE = /^[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]*\/[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]*$/;
const SHA256 = /^[a-fA-F0-9]{64}$/;
const PURPOSES: readonly WorkflowHandoffPurpose[] = [
  "idea_to_video_prompt",
  "video_prompt_to_generation",
  "review_video",
  "edit_video",
];
const STATUSES: readonly WorkflowHandoffStatus[] = ["staged", "ready", "completed", "dismissed"];
const SURFACES: readonly WorkflowSurface[] = ["chat", "work", "video"];
const MAX_ENTRIES = 500;
const MAX_TEXT_LENGTH = 131_072;
const MAX_OBJECTIVE_LENGTH = 4_096;
const MAX_ASSETS = 256;

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(record: UnknownRecord, required: readonly string[], optional: readonly string[] = []): boolean {
  const allowed = new Set([...required, ...optional]);
  return required.every((key) => Object.hasOwn(record, key))
    && Object.keys(record).every((key) => allowed.has(key));
}

function identifier(value: unknown): value is string {
  return typeof value === "string" && IDENTIFIER.test(value);
}

function isoTimestamp(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function nonBlankString(value: unknown, maxLength: number): value is string {
  return typeof value === "string" && value.length <= maxLength && value.trim().length > 0 && !value.includes("\0");
}

function boundedDraftString(value: unknown, maxLength: number): value is string {
  return typeof value === "string" && value.length <= maxLength && !value.includes("\0");
}

export function isSafeWorkspaceRelativePath(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0 || value.length > 1_024 || value.includes("\0")) return false;
  if (value.startsWith("/") || value.startsWith("\\") || /^[A-Za-z]:[\\/]/.test(value)) return false;
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(value) || value.includes("\\")) return false;
  const segments = value.split("/");
  return segments.every((segment) =>
    segment.length > 0
    && segment !== "."
    && segment !== ".."
    && !/[<>:"|?*]/.test(segment)
    && !/[. ]$/.test(segment));
}

function cloneArtifactRef(ref: WorkflowArtifactRef): WorkflowArtifactRef {
  return {
    assetId: ref.assetId,
    relativePath: ref.relativePath,
    ...(ref.artifactId === undefined ? {} : { artifactId: ref.artifactId }),
    ...(ref.mediaType === undefined ? {} : { mediaType: ref.mediaType }),
    ...(ref.sha256 === undefined ? {} : { sha256: ref.sha256 }),
  };
}

function cloneSource(source: WorkflowSource): WorkflowSource {
  return {
    surface: source.surface,
    projectId: source.projectId,
    threadId: source.threadId,
    ...(source.turnId === undefined ? {} : { turnId: source.turnId }),
    ...(source.itemId === undefined ? {} : { itemId: source.itemId }),
    ...(source.artifactId === undefined ? {} : { artifactId: source.artifactId }),
  };
}

function cloneTarget(target: WorkflowTarget): WorkflowTarget {
  return {
    surface: target.surface,
    projectId: target.projectId,
    ...(target.threadId === undefined ? {} : { threadId: target.threadId }),
  };
}

function cloneHandoff(handoff: WorkflowHandoff): WorkflowHandoff {
  return {
    ...handoff,
    source: cloneSource(handoff.source),
    target: cloneTarget(handoff.target),
    assetIds: [...handoff.assetIds],
    artifactRefs: handoff.artifactRefs.map(cloneArtifactRef),
  } as WorkflowHandoff;
}

function cloneDelivery(delivery: WorkflowDelivery): WorkflowDelivery {
  return {
    ...delivery,
    assetIds: [...delivery.assetIds],
    artifactRefs: delivery.artifactRefs.map(cloneArtifactRef),
  };
}

function validSource(value: unknown): value is WorkflowSource {
  if (!isRecord(value) || !exactKeys(value, ["surface", "projectId", "threadId"], ["turnId", "itemId", "artifactId"])) return false;
  if (!SURFACES.includes(value.surface as WorkflowSurface) || !identifier(value.projectId) || !identifier(value.threadId)) return false;
  return ["turnId", "itemId", "artifactId"].every((key) => !Object.hasOwn(value, key) || identifier(value[key]));
}

function validTarget(value: unknown): value is WorkflowTarget {
  if (!isRecord(value) || !exactKeys(value, ["surface", "projectId"], ["threadId"])) return false;
  return SURFACES.includes(value.surface as WorkflowSurface)
    && identifier(value.projectId)
    && (!Object.hasOwn(value, "threadId") || identifier(value.threadId));
}

function validArtifactRef(value: unknown, trustedAssetIds: ReadonlySet<string>): value is WorkflowArtifactRef {
  if (!isRecord(value) || !exactKeys(value, ["assetId", "relativePath"], ["artifactId", "mediaType", "sha256"])) return false;
  if (!identifier(value.assetId) || !trustedAssetIds.has(value.assetId) || !isSafeWorkspaceRelativePath(value.relativePath)) return false;
  if (Object.hasOwn(value, "artifactId") && !identifier(value.artifactId)) return false;
  if (Object.hasOwn(value, "mediaType") && (typeof value.mediaType !== "string" || !MEDIA_TYPE.test(value.mediaType))) return false;
  return !Object.hasOwn(value, "sha256") || (typeof value.sha256 === "string" && SHA256.test(value.sha256));
}

function routeIsValid(purpose: WorkflowHandoffPurpose, source: WorkflowSurface, target: WorkflowSurface): boolean {
  if (purpose === "idea_to_video_prompt") return source === "chat" && target === "work";
  if (purpose === "video_prompt_to_generation") return source === "work" && target === "video";
  return source === "video" && target === "work";
}

function normalizedNewInput(input: unknown): NewWorkflowHandoff {
  if (!isRecord(input) || !exactKeys(
    input,
    ["idempotencyKey", "correlationId", "principalId", "projectId", "purpose", "source", "target", "objective", "text"],
    ["assetIds", "artifactRefs"],
  )) {
    throw new WorkflowHandoffError("invalid_input", "Handoff input contains missing or unknown fields");
  }
  if (!identifier(input.idempotencyKey) || !identifier(input.correlationId) || !identifier(input.principalId) || !identifier(input.projectId)) {
    throw new WorkflowHandoffError("invalid_input", "Handoff identifiers are malformed");
  }
  if (!PURPOSES.includes(input.purpose as WorkflowHandoffPurpose) || !validSource(input.source) || !validTarget(input.target)) {
    throw new WorkflowHandoffError("invalid_input", "Handoff route is malformed");
  }
  const purpose = input.purpose as WorkflowHandoffPurpose;
  if (!routeIsValid(purpose, input.source.surface, input.target.surface)) {
    throw new WorkflowHandoffError("invalid_input", "Handoff purpose does not match its source and target surfaces");
  }
  if (!nonBlankString(input.objective, MAX_OBJECTIVE_LENGTH) || !nonBlankString(input.text, MAX_TEXT_LENGTH)) {
    throw new WorkflowHandoffError("invalid_input", "Handoff objective and text must be non-empty bounded strings");
  }
  const rawAssetIds = input.assetIds === undefined ? [] : input.assetIds;
  const rawRefs = input.artifactRefs === undefined ? [] : input.artifactRefs;
  if (!Array.isArray(rawAssetIds) || rawAssetIds.length > MAX_ASSETS || !rawAssetIds.every(identifier)) {
    throw new WorkflowHandoffError("invalid_input", "Handoff assetIds are malformed");
  }
  const assetIds = [...rawAssetIds] as string[];
  if (new Set(assetIds).size !== assetIds.length) {
    throw new WorkflowHandoffError("invalid_input", "Handoff assetIds must be unique");
  }
  const trusted = new Set(assetIds);
  if (!Array.isArray(rawRefs) || rawRefs.length > MAX_ASSETS || !rawRefs.every((ref) => validArtifactRef(ref, trusted))) {
    throw new WorkflowHandoffError("invalid_input", "Artifact references must point to trusted assetIds and use workspace-relative paths");
  }
  const artifactRefs = (rawRefs as WorkflowArtifactRef[]).map(cloneArtifactRef);
  if (new Set(artifactRefs.map((ref) => `${ref.assetId}\0${ref.relativePath}`)).size !== artifactRefs.length) {
    throw new WorkflowHandoffError("invalid_input", "Handoff artifact references must be unique");
  }
  return {
    idempotencyKey: input.idempotencyKey,
    correlationId: input.correlationId,
    principalId: input.principalId,
    projectId: input.projectId,
    purpose,
    source: cloneSource(input.source),
    target: cloneTarget(input.target),
    objective: input.objective,
    text: input.text,
    assetIds,
    artifactRefs,
  } as NewWorkflowHandoff;
}

function requestFingerprint(input: NewWorkflowHandoff): string {
  // The normalized object has deterministic property order and rejects unknown
  // properties. No secret or authorization decision relies on this fingerprint.
  return JSON.stringify(input);
}

function validHandoff(value: unknown): value is WorkflowHandoff {
  if (!isRecord(value) || !exactKeys(value, [
    "schemaVersion", "handoffId", "idempotencyKey", "correlationId", "principalId", "projectId",
    "purpose", "source", "target", "objective", "text", "assetIds", "artifactRefs", "createdAt", "updatedAt", "status",
  ])) return false;
  if (value.schemaVersion !== WORKFLOW_HANDOFF_SCHEMA_VERSION
    || !identifier(value.handoffId)
    || !identifier(value.idempotencyKey)
    || !identifier(value.correlationId)
    || !identifier(value.principalId)
    || !identifier(value.projectId)
    || !PURPOSES.includes(value.purpose as WorkflowHandoffPurpose)
    || !validSource(value.source)
    || !validTarget(value.target)
    || !nonBlankString(value.objective, MAX_OBJECTIVE_LENGTH)
    || !nonBlankString(value.text, MAX_TEXT_LENGTH)
    || !isoTimestamp(value.createdAt)
    || !isoTimestamp(value.updatedAt)
    || !STATUSES.includes(value.status as WorkflowHandoffStatus)) return false;
  if (Date.parse(value.updatedAt) < Date.parse(value.createdAt)) return false;
  if (!routeIsValid(value.purpose as WorkflowHandoffPurpose, value.source.surface, value.target.surface)) return false;
  if (value.source.projectId !== value.projectId || value.target.projectId !== value.projectId) return false;
  if (!Array.isArray(value.assetIds) || value.assetIds.length > MAX_ASSETS || !value.assetIds.every(identifier)) return false;
  if (new Set(value.assetIds).size !== value.assetIds.length) return false;
  const trusted = new Set(value.assetIds as string[]);
  if (!Array.isArray(value.artifactRefs) || value.artifactRefs.length > MAX_ASSETS
    || !value.artifactRefs.every((ref) => validArtifactRef(ref, trusted))) return false;
  return new Set((value.artifactRefs as WorkflowArtifactRef[]).map((ref) => `${ref.assetId}\0${ref.relativePath}`)).size === value.artifactRefs.length;
}

function validDelivery(value: unknown, handoff: WorkflowHandoff): value is WorkflowDelivery {
  if (!isRecord(value)) return false;
  const shared = ["kind", "handoffId", "correlationId", "objective", "assetIds", "artifactRefs"];
  if (value.kind === "video_draft") {
    if (!exactKeys(value, [...shared, "prompt", "billingAuthorized", "requiresExplicitGeneration"])) return false;
    if (handoff.target.surface !== "video" || value.billingAuthorized !== false || value.requiresExplicitGeneration !== true) return false;
    if (!boundedDraftString(value.prompt, MAX_TEXT_LENGTH)) return false;
  } else if (value.kind === "work_draft" || value.kind === "work_explicit_submit") {
    if (!exactKeys(value, [...shared, "text", "submit"])) return false;
    if (handoff.target.surface !== "work" || !boundedDraftString(value.text, MAX_TEXT_LENGTH)) return false;
    if (value.submit !== (value.kind === "work_explicit_submit")) return false;
  } else return false;
  return value.handoffId === handoff.handoffId
    && value.correlationId === handoff.correlationId
    && value.objective === handoff.objective
    && JSON.stringify(value.assetIds) === JSON.stringify(handoff.assetIds)
    && JSON.stringify(value.artifactRefs) === JSON.stringify(handoff.artifactRefs);
}

function parseJournal(raw: string, principalId: string, projectId: string): HandoffJournal {
  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch (error) {
    throw new WorkflowHandoffError("invalid_journal", "Workflow handoff journal is not valid JSON", { cause: error });
  }
  if (!isRecord(value)
    || !exactKeys(value, ["schemaVersion", "principalId", "projectId", "revision", "entries"])
    || value.schemaVersion !== WORKFLOW_HANDOFF_SCHEMA_VERSION
    || value.principalId !== principalId
    || value.projectId !== projectId
    || !Number.isSafeInteger(value.revision)
    || (value.revision as number) < 0
    || !Array.isArray(value.entries)
    || value.entries.length > MAX_ENTRIES) {
    throw new WorkflowHandoffError("invalid_journal", "Workflow handoff journal scope or structure is invalid");
  }
  const seenHandoffs = new Set<string>();
  const seenIdempotencyKeys = new Set<string>();
  for (const candidate of value.entries) {
    if (!isRecord(candidate) || !exactKeys(candidate, ["requestFingerprint", "handoff"], ["delivery"])
      || typeof candidate.requestFingerprint !== "string"
      || candidate.requestFingerprint.length === 0
      || candidate.requestFingerprint.length > 1_000_000
      || !validHandoff(candidate.handoff)) {
      throw new WorkflowHandoffError("invalid_journal", "Workflow handoff journal contains a malformed entry");
    }
    const handoff = candidate.handoff;
    if (handoff.principalId !== principalId || handoff.projectId !== projectId
      || seenHandoffs.has(handoff.handoffId) || seenIdempotencyKeys.has(handoff.idempotencyKey)) {
      throw new WorkflowHandoffError("invalid_journal", "Workflow handoff journal contains a foreign or duplicate entry");
    }
    const hasDelivery = Object.hasOwn(candidate, "delivery");
    if (hasDelivery && !validDelivery(candidate.delivery, handoff)) {
      throw new WorkflowHandoffError("invalid_journal", "Workflow handoff journal contains an invalid delivery descriptor");
    }
    if ((handoff.status === "ready" || handoff.status === "completed") && !hasDelivery) {
      throw new WorkflowHandoffError("invalid_journal", "Ready and completed handoffs require a delivery descriptor");
    }
    if (handoff.status === "staged" && hasDelivery) {
      throw new WorkflowHandoffError("invalid_journal", "Staged handoffs cannot already contain a delivery descriptor");
    }
    seenHandoffs.add(handoff.handoffId);
    seenIdempotencyKeys.add(handoff.idempotencyKey);
  }
  return value as unknown as HandoffJournal;
}

function resolveStorage(storage: WorkflowHandoffStorage | undefined): WorkflowHandoffStorage {
  if (storage) return storage;
  const candidate = (globalThis as typeof globalThis & { localStorage?: WorkflowHandoffStorage }).localStorage;
  if (!candidate) throw new WorkflowHandoffError("persistence_failed", "Persistent storage is unavailable");
  return candidate;
}

function defaultCreateId(): string {
  if (!globalThis.crypto?.randomUUID) {
    throw new WorkflowHandoffError("invalid_input", "A cryptographic UUID generator is unavailable");
  }
  return globalThis.crypto.randomUUID();
}

export function workflowHandoffStorageKey(projectId: string, principalId: string): string {
  if (!identifier(projectId) || !identifier(principalId)) {
    throw new WorkflowHandoffError("invalid_input", "Project and principal IDs are malformed");
  }
  return `${WORKFLOW_HANDOFF_STORAGE_PREFIX}:${encodeURIComponent(projectId)}:${encodeURIComponent(principalId)}`;
}

export class WorkflowHandoffStore {
  readonly principalId: string;
  readonly projectId: string;
  readonly storageKey: string;

  private readonly storage: WorkflowHandoffStorage;
  private readonly now: () => string;
  private readonly createId: () => string;
  private readonly listeners = new Set<Listener>();
  private journal: HandoffJournal;

  constructor(options: WorkflowHandoffStoreOptions) {
    if (!identifier(options.principalId) || !identifier(options.projectId)) {
      throw new WorkflowHandoffError("invalid_input", "Project and principal IDs are malformed");
    }
    this.principalId = options.principalId;
    this.projectId = options.projectId;
    this.storageKey = workflowHandoffStorageKey(options.projectId, options.principalId);
    this.storage = resolveStorage(options.storage);
    this.now = options.now ?? (() => new Date().toISOString());
    this.createId = options.createId ?? defaultCreateId;
    this.journal = this.readJournal();
  }

  subscribe(listener: Listener, emitRecovered = true): () => void {
    this.listeners.add(listener);
    if (emitRecovered) listener({ type: "recovered", handoffs: this.list() });
    return () => this.listeners.delete(listener);
  }

  list(options: { includeDismissed?: boolean } = {}): WorkflowHandoff[] {
    return this.journal.entries
      .map((entry) => entry.handoff)
      .filter((handoff) => options.includeDismissed || handoff.status !== "dismissed")
      .map(cloneHandoff);
  }

  get(handoffId: string): WorkflowHandoff | null {
    if (!identifier(handoffId)) return null;
    const entry = this.journal.entries.find((candidate) => candidate.handoff.handoffId === handoffId);
    return entry ? cloneHandoff(entry.handoff) : null;
  }

  recover(): WorkflowHandoff[] {
    this.journal = this.readJournal();
    const handoffs = this.list();
    this.emit({ type: "recovered", handoffs });
    return handoffs;
  }

  stage(input: NewWorkflowHandoff): WorkflowHandoff {
    const normalized = normalizedNewInput(input);
    this.assertScope(normalized.principalId, normalized.projectId, normalized.source.projectId, normalized.target.projectId);
    const fingerprint = requestFingerprint(normalized);
    const existing = this.journal.entries.find((entry) => entry.handoff.idempotencyKey === normalized.idempotencyKey);
    if (existing) {
      if (existing.requestFingerprint !== fingerprint) {
        throw new WorkflowHandoffError("idempotency_conflict", "Idempotency key was already used for a different handoff");
      }
      return cloneHandoff(existing.handoff);
    }
    const handoffId = this.createId();
    const createdAt = this.now();
    if (!identifier(handoffId) || !isoTimestamp(createdAt)) {
      throw new WorkflowHandoffError("invalid_input", "ID generator or clock returned an invalid value");
    }
    const handoff = {
      schemaVersion: WORKFLOW_HANDOFF_SCHEMA_VERSION,
      handoffId,
      idempotencyKey: normalized.idempotencyKey,
      correlationId: normalized.correlationId,
      principalId: normalized.principalId,
      projectId: normalized.projectId,
      purpose: normalized.purpose,
      source: normalized.source,
      target: normalized.target,
      objective: normalized.objective,
      text: normalized.text,
      assetIds: normalized.assetIds ? [...normalized.assetIds] : [],
      artifactRefs: normalized.artifactRefs ? normalized.artifactRefs.map(cloneArtifactRef) : [],
      createdAt,
      updatedAt: createdAt,
      status: "staged" as const,
    } as WorkflowHandoff;
    const next = this.cloneJournal();
    if (next.entries.length >= MAX_ENTRIES) {
      const terminalIndex = next.entries.findIndex(({ handoff: candidate }) => (
        candidate.status === "completed" || candidate.status === "dismissed"
      ));
      if (terminalIndex < 0) {
        throw new WorkflowHandoffError(
          "invalid_transition",
          "Workflow handoff journal is full of active handoffs; complete or dismiss one before continuing",
        );
      }
      next.entries.splice(terminalIndex, 1);
    }
    next.revision += 1;
    next.entries.push({ requestFingerprint: fingerprint, handoff });
    this.commit(next);
    const result = cloneHandoff(handoff);
    this.emit({ type: "staged", handoff: result });
    return result;
  }

  /**
   * Builds a target-specific descriptor and persists it before notifying the
   * integration layer. This method performs no RPC and has no provider client.
   */
  prepareDelivery(handoffId: string, options: { workDisposition?: WorkDeliveryDisposition } = {}): WorkflowDelivery {
    const current = this.requireEntry(handoffId);
    if (current.handoff.status === "dismissed") {
      throw new WorkflowHandoffError("invalid_transition", "A dismissed handoff cannot be delivered");
    }
    const expectedKind = current.handoff.target.surface === "video"
      ? "video_draft"
      : options.workDisposition === "explicit-submit" ? "work_explicit_submit" : "work_draft";
    if (current.delivery) {
      if (current.delivery.kind !== expectedKind) {
        throw new WorkflowHandoffError("invalid_transition", "Handoff was already prepared with a different disposition");
      }
      return cloneDelivery(current.delivery);
    }
    if (current.handoff.status !== "staged") {
      throw new WorkflowHandoffError("invalid_transition", "Only a staged handoff can be prepared");
    }
    if (current.handoff.target.surface === "video" && options.workDisposition !== undefined) {
      throw new WorkflowHandoffError("invalid_input", "Work disposition is invalid for a video target");
    }
    if (current.handoff.target.surface === "work"
      && options.workDisposition !== undefined
      && options.workDisposition !== "draft"
      && options.workDisposition !== "explicit-submit") {
      throw new WorkflowHandoffError("invalid_input", "Unknown work delivery disposition");
    }
    const handoff = current.handoff;
    const common = {
      handoffId: handoff.handoffId,
      correlationId: handoff.correlationId,
      objective: handoff.objective,
      assetIds: [...handoff.assetIds],
      artifactRefs: handoff.artifactRefs.map(cloneArtifactRef),
    };
    const delivery: WorkflowDelivery = handoff.target.surface === "video"
      ? {
          kind: "video_draft",
          ...common,
          prompt: handoff.text,
          billingAuthorized: false,
          requiresExplicitGeneration: true,
        }
      : options.workDisposition === "explicit-submit"
        ? { kind: "work_explicit_submit", ...common, text: handoff.text, submit: true }
        : { kind: "work_draft", ...common, text: handoff.text, submit: false };
    const next = this.cloneJournal();
    const target = next.entries.find((entry) => entry.handoff.handoffId === handoffId)!;
    target.delivery = delivery;
    target.handoff.status = "ready";
    target.handoff.updatedAt = this.checkedNow(target.handoff.createdAt);
    next.revision += 1;
    this.commit(next);
    const readyHandoff = cloneHandoff(target.handoff);
    const readyDelivery = cloneDelivery(delivery);
    this.emit({ type: "delivery-ready", handoff: readyHandoff, delivery: readyDelivery });
    return readyDelivery;
  }

  /**
   * Persists the editable target draft without mutating the immutable source
   * handoff text or authorizing any downstream action.
   */
  updateDraft(handoffId: string, text: string): WorkflowDelivery {
    if (!boundedDraftString(text, MAX_TEXT_LENGTH)) {
      throw new WorkflowHandoffError("invalid_input", "Workflow draft text is malformed or too large");
    }
    const current = this.requireEntry(handoffId);
    if (current.handoff.status !== "ready" || !current.delivery) {
      throw new WorkflowHandoffError("invalid_transition", "Only a ready handoff draft can be updated");
    }
    const existingText = current.delivery.kind === "video_draft" ? current.delivery.prompt : current.delivery.text;
    if (existingText === text) return cloneDelivery(current.delivery);
    const next = this.cloneJournal();
    const target = next.entries.find((entry) => entry.handoff.handoffId === handoffId)!;
    target.delivery = target.delivery!.kind === "video_draft"
      ? { ...target.delivery!, prompt: text }
      : { ...target.delivery!, text };
    target.handoff.updatedAt = this.checkedNow(target.handoff.createdAt);
    next.revision += 1;
    this.commit(next);
    return cloneDelivery(target.delivery);
  }

  complete(handoffId: string): WorkflowHandoff {
    const current = this.requireEntry(handoffId);
    if (current.handoff.status === "completed") return cloneHandoff(current.handoff);
    if (current.handoff.status !== "ready" || !current.delivery) {
      throw new WorkflowHandoffError("invalid_transition", "Only a ready handoff can be completed");
    }
    const next = this.cloneJournal();
    const target = next.entries.find((entry) => entry.handoff.handoffId === handoffId)!;
    target.handoff.status = "completed";
    target.handoff.updatedAt = this.checkedNow(target.handoff.createdAt);
    next.revision += 1;
    this.commit(next);
    const result = cloneHandoff(target.handoff);
    this.emit({ type: "completed", handoff: result });
    return result;
  }

  dismiss(handoffId: string): WorkflowHandoff {
    const current = this.requireEntry(handoffId);
    if (current.handoff.status === "dismissed") return cloneHandoff(current.handoff);
    if (current.handoff.status === "completed") {
      throw new WorkflowHandoffError("invalid_transition", "A completed handoff cannot be dismissed");
    }
    const next = this.cloneJournal();
    const target = next.entries.find((entry) => entry.handoff.handoffId === handoffId)!;
    target.handoff.status = "dismissed";
    target.handoff.updatedAt = this.checkedNow(target.handoff.createdAt);
    next.revision += 1;
    this.commit(next);
    const result = cloneHandoff(target.handoff);
    this.emit({ type: "dismissed", handoff: result });
    return result;
  }

  private assertScope(...projectAndPrincipalIds: string[]): void {
    if (projectAndPrincipalIds[0] !== this.principalId || projectAndPrincipalIds.slice(1).some((id) => id !== this.projectId)) {
      throw new WorkflowHandoffError("scope_mismatch", "Cross-principal or cross-project handoffs are forbidden");
    }
  }

  private requireEntry(handoffId: string): JournalEntry {
    if (!identifier(handoffId)) throw new WorkflowHandoffError("not_found", "Handoff was not found");
    const entry = this.journal.entries.find((candidate) => candidate.handoff.handoffId === handoffId);
    if (!entry) throw new WorkflowHandoffError("not_found", "Handoff was not found");
    return entry;
  }

  private checkedNow(createdAt: string): string {
    const value = this.now();
    if (!isoTimestamp(value) || Date.parse(value) < Date.parse(createdAt)) {
      throw new WorkflowHandoffError("invalid_input", "Clock returned an invalid timestamp");
    }
    return value;
  }

  private readJournal(): HandoffJournal {
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.storageKey);
    } catch (error) {
      throw new WorkflowHandoffError("persistence_failed", "Workflow handoff journal cannot be read", { cause: error });
    }
    if (raw === null) {
      return {
        schemaVersion: WORKFLOW_HANDOFF_SCHEMA_VERSION,
        principalId: this.principalId,
        projectId: this.projectId,
        revision: 0,
        entries: [],
      };
    }
    return parseJournal(raw, this.principalId, this.projectId);
  }

  private cloneJournal(): HandoffJournal {
    return {
      schemaVersion: WORKFLOW_HANDOFF_SCHEMA_VERSION,
      principalId: this.principalId,
      projectId: this.projectId,
      revision: this.journal.revision,
      entries: this.journal.entries.map((entry) => ({
        requestFingerprint: entry.requestFingerprint,
        handoff: cloneHandoff(entry.handoff),
        ...(entry.delivery === undefined ? {} : { delivery: cloneDelivery(entry.delivery) }),
      })),
    };
  }

  private commit(next: HandoffJournal): void {
    const serialized = JSON.stringify(next);
    try {
      this.storage.setItem(this.storageKey, serialized);
      if (this.storage.getItem(this.storageKey) !== serialized) {
        throw new Error("journal write verification failed");
      }
    } catch (error) {
      throw new WorkflowHandoffError("persistence_failed", "Workflow handoff journal could not be durably written", { cause: error });
    }
    this.journal = next;
  }

  private emit(event: WorkflowHandoffEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}
